// Regras de calculo do controle financeiro.
// Espelham as formulas da planilha "Controle_Financeiro_Barbearia_Lisboa.xlsx"
// e ficam separadas do Firestore para poderem ser testadas isoladamente.

export const STATUS_RECEITA = ['Recebido', 'Pendente', 'Cancelado']
export const STATUS_DESPESA = ['Pago', 'Em aberto']
export const TIPOS_DESPESA = ['Fixa', 'Variável']
export const APROPRIACOES_DESPESA = ['Direta', 'Indireta']
export const CATEGORIA_TAXA_CREDITO = 'Taxa de crédito'

export const CONFIG_PADRAO = {
    nomeBarbearia: 'Barbearia Lisboa',
    taxaDebito: 0,
    taxaCredito: 0,
    metaMargem: 0.2,
    formasPagamento: ['Dinheiro', 'PIX', 'Débito', 'Crédito', 'Pacote', 'Cortesia', 'Outro'],
    categoriasServico: ['Corte', 'Barba', 'Corte + Barba', 'Acabamento', 'Sobrancelha', 'Produto', 'Outro'],
    categoriasDespesa: ['Aluguel', 'Água / Luz', 'Internet', 'Sistema', 'Contador', 'Produtos', 'Material descartável', 'Marketing', 'Manutenção', 'Impostos', 'Taxa de crédito', 'Outro'],
    mapaCategorias: {}
}

export const COMISSAO_PADRAO = {
    corte: 0.4,
    barba: 0.4,
    corteBarba: 0.4,
    outros: 0.4,
    meta: 0
}

// O portal grava preco como string no formato "80.00" (ponto decimal), que e o
// que o vuetify-money produz. Tratar o ponto como separador de milhar inflava
// os valores em 100x, entao so a virgula e considerada separador decimal.
export function numero(valor){
    if(typeof valor === 'number'){
        return Number.isFinite(valor) ? valor : 0
    }

    if(typeof valor !== 'string'){
        return 0
    }

    const texto = valor.trim()
    if(!texto){
        return 0
    }

    // "1.234,56" -> ponto e separador de milhar; "80.00" -> ponto e decimal.
    const normalizado = texto.includes(',')
        ? texto.replace(/\./g, '').replace(',', '.')
        : texto

    const convertido = parseFloat(normalizado)
    return Number.isFinite(convertido) ? convertido : 0
}

// 'YYYY-MM-DD' -> 'YYYY-MM'
export function chaveMes(data){
    if(!data){
        return ''
    }
    return String(data).substring(0, 7)
}

export function primeiroDiaDoMes(mes){
    return `${mes}-01`
}

export function ultimoDiaDoMes(mes){
    const [ano, numeroMes] = mes.split('-').map(Number)
    const data = new Date(ano, numeroMes, 0)
    const dia = `${data.getDate()}`.padStart(2, '0')
    return `${mes}-${dia}`
}

// Descobre a categoria de comissao a partir da descricao do servico do portal.
// Usada apenas como sugestao na importacao da agenda; o usuario pode corrigir
// e a correcao fica salva em config.mapaCategorias.
export function sugerirCategoria(descricao){
    const texto = String(descricao || '').toLowerCase()
    const temCorte = texto.includes('corte') || texto.includes('cabelo')
    const temBarba = texto.includes('barba')

    if(temCorte && temBarba){
        return 'Corte + Barba'
    }
    if(temBarba){
        return 'Barba'
    }
    if(temCorte){
        return 'Corte'
    }
    return 'Outro'
}

// Planilha: H = IF(pagamento="Débito";taxaDebito;IF(pagamento="Crédito";taxaCredito;0))
export function taxaDoPagamento(pagamento, config){
    if(pagamento === 'Débito'){
        return numero(config.taxaDebito)
    }
    if(pagamento === 'Crédito'){
        return numero(config.taxaCredito)
    }
    return 0
}

// Permite informar no mês o total da taxa de crédito cobrada pela maquininha,
// sem precisar classificar cada receita individualmente.
export function ehTaxaCartaoManual(despesa){
    return String((despesa || {}).categoria || '').trim() === CATEGORIA_TAXA_CREDITO
}

// Planilha: K = INDEX/MATCH do percentual do barbeiro conforme a categoria
export function percentualComissao(categoria, comissaoBarbeiro){
    const comissao = comissaoBarbeiro || {}

    if(categoria === 'Corte'){
        return numero(comissao.corte)
    }
    if(categoria === 'Barba'){
        return numero(comissao.barba)
    }
    if(categoria === 'Corte + Barba'){
        return numero(comissao.corteBarba)
    }
    return numero(comissao.outros)
}

// Colunas H a M da aba Atendimentos.
export function calcularReceita(receita, config, comissoes){
    const item = receita || {}
    const configuracao = config || CONFIG_PADRAO
    const comissaoBarbeiro = (comissoes || {})[item.barbeiroId]

    const valorBruto = numero(item.valorBruto)
    const desconto = numero(item.desconto)
    const baseComissao = Math.max(0, valorBruto - desconto)
    const taxaPercentual = taxaDoPagamento(item.pagamento, configuracao)
    const taxaValor = baseComissao * taxaPercentual
    const percentual = percentualComissao(item.categoria, comissaoBarbeiro)
    const comissao = baseComissao * percentual
    const liquido = baseComissao - taxaValor - comissao

    return {
        ...item,
        valorBruto,
        desconto,
        baseComissao,
        taxaPercentual,
        taxaValor,
        percentualComissao: percentual,
        comissao,
        liquido
    }
}

export function calcularReceitas(receitas, config, comissoes){
    return (receitas || []).map((item) => calcularReceita(item, config, comissoes))
}

function somar(lista, campo){
    return lista.reduce((acc, item) => acc + numero(item[campo]), 0)
}

// Aba "Painel Mensal". Considera apenas receitas "Recebido" e despesas "Pago",
// exatamente como os SUMIFS da planilha.
export function resumoMensal(receitas, despesas, config, comissoes, barbeiros){
    const calculadas = calcularReceitas(receitas, config, comissoes)
    const recebidas = calculadas.filter((item) => item.status === 'Recebido')
    const pagas = (despesas || []).filter((item) => item.status === 'Pago')
    const taxasCartaoManuais = pagas.filter(ehTaxaCartaoManual)
    const despesasOperacionaisPagas = pagas.filter((item) => !ehTaxaCartaoManual(item))

    const faturamentoBruto = somar(recebidas, 'valorBruto')
    const descontos = somar(recebidas, 'desconto')
    const taxasCartao = somar(recebidas, 'taxaValor') + somar(taxasCartaoManuais, 'valor')
    const comissoesTotal = somar(recebidas, 'comissao')
    const receitaAposComissoes = faturamentoBruto - descontos - taxasCartao - comissoesTotal
    const despesasPagas = somar(pagas, 'valor')
    const despesasOperacionais = somar(despesasOperacionaisPagas, 'valor')
    const lucro = receitaAposComissoes - despesasOperacionais
    const faturamentoLiquido = faturamentoBruto - descontos
    const margem = faturamentoLiquido ? lucro / faturamentoLiquido : 0

    const porClassificacao = [
        { rotulo: 'Taxas de cartão', valor: somar(taxasCartaoManuais, 'valor') },
        { rotulo: 'Fixas', valor: somar(despesasOperacionaisPagas.filter((item) => item.tipo === 'Fixa'), 'valor') },
        { rotulo: 'Variáveis', valor: somar(despesasOperacionaisPagas.filter((item) => item.tipo === 'Variável'), 'valor') },
        { rotulo: 'Diretas', valor: somar(despesasOperacionaisPagas.filter((item) => item.apropriacao === 'Direta'), 'valor') },
        { rotulo: 'Indiretas', valor: somar(despesasOperacionaisPagas.filter((item) => item.apropriacao === 'Indireta'), 'valor') }
    ].map((item) => ({
        ...item,
        percentual: despesasPagas ? item.valor / despesasPagas : 0
    }))

    // Lancamentos livres nao tem barbeiro; entram numa linha propria para que a
    // soma da tabela continue fechando com o faturamento do mes.
    const semBarbeiro = recebidas.filter((item) => {
        return !(barbeiros || []).some((barbeiro) => barbeiro.id === item.barbeiroId)
    })

    const porBarbeiro = (barbeiros || []).map((barbeiro) => {
        const doBarbeiro = recebidas.filter((item) => item.barbeiroId === barbeiro.id)
        const faturamento = somar(doBarbeiro, 'valorBruto')
        const meta = numero(((comissoes || {})[barbeiro.id] || {}).meta)

        return {
            id: barbeiro.id,
            nome: barbeiro.nome,
            quantidade: doBarbeiro.length,
            faturamento,
            comissao: somar(doBarbeiro, 'comissao'),
            liquido: somar(doBarbeiro, 'liquido'),
            meta,
            percentualMeta: meta ? faturamento / meta : 0
        }
    }).sort((a, b) => a.faturamento > b.faturamento ? -1 : 1)

    if(semBarbeiro.length){
        porBarbeiro.push({
            id: 'sem-barbeiro',
            nome: 'Sem barbeiro (lançamentos livres)',
            quantidade: semBarbeiro.length,
            faturamento: somar(semBarbeiro, 'valorBruto'),
            comissao: somar(semBarbeiro, 'comissao'),
            liquido: somar(semBarbeiro, 'liquido'),
            meta: 0,
            percentualMeta: 0
        })
    }

    const porCategoria = {}
    recebidas.forEach((item) => {
        const chave = item.categoria || 'Outro'
        if(!porCategoria[chave]){
            porCategoria[chave] = { categoria: chave, quantidade: 0, faturamento: 0, liquido: 0 }
        }
        porCategoria[chave].quantidade += 1
        porCategoria[chave].faturamento += numero(item.valorBruto)
        porCategoria[chave].liquido += numero(item.liquido)
    })

    return {
        faturamentoBruto,
        descontos,
        taxasCartao,
        comissoes: comissoesTotal,
        receitaAposComissoes,
        despesasPagas,
        despesasOperacionais,
        lucro,
        margem,
        metaMargem: numero((config || CONFIG_PADRAO).metaMargem),
        atingiuMetaMargem: margem >= numero((config || CONFIG_PADRAO).metaMargem),
        quantidadeRecebidos: recebidas.length,
        quantidadePendentes: calculadas.filter((item) => item.status === 'Pendente').length,
        despesasEmAberto: somar((despesas || []).filter((item) => item.status === 'Em aberto'), 'valor'),
        porClassificacao,
        porBarbeiro,
        porCategoria: Object.values(porCategoria).sort((a, b) => a.faturamento > b.faturamento ? -1 : 1)
    }
}

// Aba "Conferência": aponta lancamentos incompletos antes do fechamento.
export function conferencia(receitas, despesas, comissoes){
    const listaReceitas = receitas || []
    const listaDespesas = despesas || []

    const percentuaisInvalidos = Object.values(comissoes || {}).reduce((acc, item) => {
        const valores = [item.corte, item.barba, item.corteBarba, item.outros]
        return acc + valores.filter((valor) => numero(valor) < 0 || numero(valor) > 1).length
    }, 0)

    const controles = [
        {
            rotulo: 'Receitas sem data',
            quantidade: listaReceitas.filter((item) => !item.data).length,
            onde: 'Receitas'
        },
        {
            // Lancamento livre pode nao ter barbeiro de proposito; so o que veio
            // da agenda deveria sempre ter.
            rotulo: 'Receitas da agenda sem barbeiro',
            quantidade: listaReceitas.filter((item) => item.origem === 'agenda' && !item.barbeiroId).length,
            onde: 'Receitas'
        },
        {
            rotulo: 'Receitas recebidas sem valor',
            quantidade: listaReceitas.filter((item) => item.status === 'Recebido' && numero(item.valorBruto) <= 0).length,
            onde: 'Receitas'
        },
        {
            rotulo: 'Receitas sem forma de pagamento',
            quantidade: listaReceitas.filter((item) => item.status === 'Recebido' && !item.pagamento).length,
            onde: 'Receitas'
        },
        {
            rotulo: 'Despesas pagas sem valor',
            quantidade: listaDespesas.filter((item) => item.status === 'Pago' && numero(item.valor) <= 0).length,
            onde: 'Despesas'
        },
        {
            rotulo: 'Comissões fora de 0% a 100%',
            quantidade: percentuaisInvalidos,
            onde: 'Equipe'
        }
    ]

    return {
        controles: controles.map((item) => ({ ...item, ok: item.quantidade === 0 })),
        aprovado: controles.every((item) => item.quantidade === 0)
    }
}

// Achata o resumo do mes no documento que fica gravado como cache consolidado.
// Guarda so o que a visao de periodo precisa, sem os lancamentos individuais.
export function montarResumoArmazenavel(mes, resumo){
    return {
        mes,
        faturamentoBruto: resumo.faturamentoBruto,
        descontos: resumo.descontos,
        taxasCartao: resumo.taxasCartao,
        comissoes: resumo.comissoes,
        receitaAposComissoes: resumo.receitaAposComissoes,
        despesasOperacionais: resumo.despesasOperacionais,
        despesasEmAberto: resumo.despesasEmAberto,
        lucro: resumo.lucro,
        margem: resumo.margem,
        quantidadeRecebidos: resumo.quantidadeRecebidos,
        quantidadePendentes: resumo.quantidadePendentes,
        porBarbeiro: resumo.porBarbeiro.map((item) => ({
            id: item.id,
            nome: item.nome,
            quantidade: item.quantidade,
            faturamento: item.faturamento,
            comissao: item.comissao,
            liquido: item.liquido
        })),
        porCategoria: resumo.porCategoria.map((item) => ({
            categoria: item.categoria,
            quantidade: item.quantidade,
            faturamento: item.faturamento,
            liquido: item.liquido
        })),
        porClassificacao: resumo.porClassificacao.map((item) => ({
            rotulo: item.rotulo,
            valor: item.valor
        }))
    }
}

// Assinatura usada para decidir se o cache precisa ser regravado.
export function assinaturaResumo(resumo){
    return JSON.stringify(montarResumoArmazenavel(resumo.mes || '', resumo))
}

// Soma varios resumos mensais em um unico consolidado de periodo.
export function consolidarPeriodo(resumos){
    const lista = resumos || []
    const zero = {
        faturamentoBruto: 0,
        descontos: 0,
        taxasCartao: 0,
        comissoes: 0,
        receitaAposComissoes: 0,
        despesasOperacionais: 0,
        despesasEmAberto: 0,
        lucro: 0,
        quantidadeRecebidos: 0,
        quantidadePendentes: 0
    }

    const totais = lista.reduce((acc, item) => {
        Object.keys(zero).forEach((chave) => {
            acc[chave] += numero(item[chave])
        })
        return acc
    }, { ...zero })

    const faturamentoLiquido = totais.faturamentoBruto - totais.descontos
    totais.margem = faturamentoLiquido ? totais.lucro / faturamentoLiquido : 0
    totais.meses = lista.length
    totais.mediaFaturamento = lista.length ? totais.faturamentoBruto / lista.length : 0
    totais.mediaLucro = lista.length ? totais.lucro / lista.length : 0

    const agrupar = (campoChave, campoNome) => {
        const mapa = {}
        lista.forEach((resumo) => {
            const colecao = campoChave === 'categoria' ? resumo.porCategoria : resumo.porBarbeiro
            ;(colecao || []).forEach((item) => {
                const chave = item[campoNome] || 'Sem identificação'
                if(!mapa[chave]){
                    mapa[chave] = { nome: chave, quantidade: 0, faturamento: 0, liquido: 0 }
                }
                mapa[chave].quantidade += numero(item.quantidade)
                mapa[chave].faturamento += numero(item.faturamento)
                mapa[chave].liquido += numero(item.liquido)
            })
        })
        return Object.values(mapa).sort((a, b) => a.faturamento > b.faturamento ? -1 : 1)
    }

    return {
        ...totais,
        melhorMes: lista.slice().sort((a, b) => numero(a.lucro) > numero(b.lucro) ? -1 : 1)[0] || null,
        porBarbeiro: agrupar('barbeiro', 'nome'),
        porCategoria: agrupar('categoria', 'categoria')
    }
}

// Lista de meses 'YYYY-MM' entre dois extremos, inclusive.
export function mesesDoIntervalo(mesInicial, mesFinal){
    if(!mesInicial || !mesFinal || mesInicial > mesFinal){
        return []
    }

    const meses = []
    let [ano, mes] = mesInicial.split('-').map(Number)
    const limite = mesFinal

    // Trava de seguranca: no maximo 120 meses (10 anos).
    for(let i = 0; i < 120; i++){
        const atual = `${ano}-${`${mes}`.padStart(2, '0')}`
        meses.push(atual)

        if(atual >= limite){
            break
        }

        mes += 1
        if(mes > 12){
            mes = 1
            ano += 1
        }
    }

    return meses
}

export function nomeDoMes(mes){
    if(!mes){
        return ''
    }
    const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
    const [ano, numeroMes] = mes.split('-')
    return `${nomes[Number(numeroMes) - 1]}/${String(ano).substring(2)}`
}

export function formatarMoeda(valor){
    return `R$ ${numero(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function formatarPercentual(valor){
    return `${(numero(valor) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
}
